import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  CheckCircle2,
  CircleSlash,
  ClipboardList,
  Clock,
  HeartPulse,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui";
import { api } from "../lib/api";
import type { BenefitEnrollment } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

const STATUSES = [
  "draft",
  "eligibility_checked",
  "submitted",
  "pending_review",
  "approved",
  "effective",
  "closed",
  "rejected",
  "cancelled",
] as const;

const TONE_BY_STATUS: Record<string, "amber" | "emerald" | "rose" | "slate"> = {
  draft: "slate",
  eligibility_checked: "slate",
  submitted: "amber",
  pending_review: "amber",
  approved: "emerald",
  effective: "emerald",
  closed: "slate",
  rejected: "rose",
  cancelled: "rose",
};

function StatusPill({ status }: { status: string }) {
  const tone = TONE_BY_STATUS[status] ?? "slate";
  const cls =
    tone === "emerald"
      ? "bg-emerald-100 text-emerald-800"
      : tone === "amber"
      ? "bg-amber-100 text-amber-800"
      : tone === "rose"
      ? "bg-rose-100 text-rose-800"
      : "bg-slate-100 text-slate-700";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${cls}`}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

function TriggerIcon({ kind }: { kind: string }) {
  if (kind === "annual_window") return <CalendarDays className="h-4 w-4" />;
  if (kind === "new_hire") return <ClipboardList className="h-4 w-4" />;
  return <HeartPulse className="h-4 w-4" />;
}

function EnrollmentRow({
  enr,
  onAction,
}: {
  enr: BenefitEnrollment;
  onAction: (action: string, body?: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-border-subtle bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <TriggerIcon kind={enr.trigger_kind} />
          <div>
            <div className="text-sm font-medium text-text-primary">
              {enr.employee_id.slice(0, 8)}…
            </div>
            <div className="text-xs text-text-tertiary">
              {enr.trigger_kind.replace(/_/g, " ")}
              {enr.qle_kind ? ` · ${enr.qle_kind}` : ""}
            </div>
          </div>
        </div>
        <StatusPill status={enr.status} />
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-text-secondary">
        <div>
          <dt className="text-text-tertiary">{t("benefits.effective_from", "Effective from")}</dt>
          <dd>{enr.effective_from}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">{t("benefits.effective_until", "Effective until")}</dt>
          <dd>{enr.effective_until ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">{t("benefits.employee_contrib", "Employee/cycle")}</dt>
          <dd className="font-mono">{enr.employee_contribution_per_cycle}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">{t("benefits.employer_contrib", "Employer/cycle")}</dt>
          <dd className="font-mono">{enr.employer_contribution_per_cycle}</dd>
        </div>
        {enr.rejection_reason && (
          <div className="col-span-2">
            <dt className="text-text-tertiary">{t("benefits.rejection_reason", "Rejection")}</dt>
            <dd className="text-rose-700">{enr.rejection_reason}</dd>
          </div>
        )}
      </dl>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {enr.status === "draft" && (
          <button
            type="button"
            onClick={() => onAction("submit")}
            className="rounded-lg bg-brand-deep px-2 py-1 text-[11px] font-medium text-white hover:opacity-95"
          >
            <Clock className="mr-1 inline h-3 w-3" />
            {t("benefits.submit", "Submit")}
          </button>
        )}
        {enr.status === "pending_review" && (
          <>
            <button
              type="button"
              onClick={() =>
                onAction("approve", {
                  approver_clerk_user_id: "hr_director",
                })
              }
              className="rounded-lg bg-emerald-700 px-2 py-1 text-[11px] font-medium text-white hover:opacity-95"
            >
              <CheckCircle2 className="mr-1 inline h-3 w-3" />
              {t("benefits.approve", "Approve")}
            </button>
            <button
              type="button"
              onClick={() => {
                const reason = window.prompt(
                  t(
                    "benefits.reject_reason_prompt",
                    "Rejection reason (will be audit-logged)",
                  ),
                );
                if (reason && reason.trim().length > 0) {
                  onAction("reject", {
                    rejector_clerk_user_id: "hr_director",
                    reason,
                  });
                }
              }}
              className="rounded-lg border border-rose-300 px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-50"
            >
              <XCircle className="mr-1 inline h-3 w-3" />
              {t("benefits.reject", "Reject")}
            </button>
          </>
        )}
        {enr.status === "approved" && (
          <button
            type="button"
            onClick={() => onAction("effective")}
            className="rounded-lg bg-emerald-700 px-2 py-1 text-[11px] font-medium text-white hover:opacity-95"
          >
            {t("benefits.make_effective", "Make effective")}
          </button>
        )}
        {enr.status === "effective" && (
          <button
            type="button"
            onClick={() => onAction("close")}
            className="rounded-lg border border-border-subtle px-2 py-1 text-[11px] font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("benefits.close", "Close")}
          </button>
        )}
        {["draft", "eligibility_checked", "submitted", "pending_review", "approved"].includes(
          enr.status,
        ) && (
          <button
            type="button"
            onClick={() => onAction("cancel")}
            className="rounded-lg border border-border-subtle px-2 py-1 text-[11px] text-text-tertiary hover:bg-surface-sunken"
          >
            <CircleSlash className="mr-1 inline h-3 w-3" />
            {t("benefits.cancel", "Cancel")}
          </button>
        )}
      </div>
    </div>
  );
}

export function BenefitEnrollmentsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [statusFilter, setStatusFilter] = useState<string>("");

  const list = useQuery({
    queryKey: ["benefit-enrollments", legalEntityId, statusFilter],
    queryFn: () =>
      api.listBenefitEnrollments(legalEntityId ?? "", {
        status: statusFilter || undefined,
      }),
    enabled: !!legalEntityId,
  });

  const action = useMutation({
    mutationFn: ({
      id,
      action,
      body,
    }: {
      id: string;
      action: string;
      body?: Record<string, unknown>;
    }) => api.benefitEnrollmentAction(id, action, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["benefit-enrollments"] });
    },
  });

  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    (list.data ?? []).forEach((e) => {
      map[e.status] = (map[e.status] ?? 0) + 1;
    });
    return map;
  }, [list.data]);

  if (gate) return gate;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("benefits.enrollments_title", "Benefits enrollments")}
        subtitle={t(
          "benefits.enrollments_subtitle",
          "Annual / new-hire / qualifying-life-event enrollments across all plans in the legal entity. Approvals + state transitions audit-logged.",
        )}
      />

      <div className="flex flex-wrap gap-1 border-b border-border-subtle">
        <button
          type="button"
          onClick={() => setStatusFilter("")}
          className={`-mb-px rounded-t-lg border-b-2 px-3 py-1 text-xs ${
            statusFilter === ""
              ? "border-brand-blue text-brand-deep"
              : "border-transparent text-text-secondary hover:text-text-primary"
          }`}
        >
          {t("benefits.all", "All")} ({list.data?.length ?? "—"})
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`-mb-px rounded-t-lg border-b-2 px-3 py-1 text-xs capitalize ${
              statusFilter === s
                ? "border-brand-blue text-brand-deep"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            {s.replace(/_/g, " ")}{" "}
            <span className="text-text-tertiary">({counts[s] ?? 0})</span>
          </button>
        ))}
      </div>

      {list.isLoading && <Loading />}
      {list.isError && (
        <ErrorPanel message={(list.error as Error).message} />
      )}
      {list.data && list.data.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
          {t(
            "benefits.no_enrollments",
            "No enrollments in this status. HR-initiated enrollments land here once submitted via the API or the future /me/benefits self-service page.",
          )}
        </div>
      )}
      {list.data && list.data.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.data.map((enr) => (
            <EnrollmentRow
              key={enr.id}
              enr={enr}
              onAction={(act, body) =>
                action.mutate({ id: enr.id, action: act, body })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
